/**
 * Created by neo on 02.12.20.
 *
 * Simplified model matching backend WeekDay.kt
 * Backend pre-resolves all display properties on entries.
 */
import { makeAutoObservable } from 'mobx';
import { WeekDayEntry } from './WeekDayEntry';
import { WeekDayEntryBuilder } from './WeekDayEntryBuilder';
import { notUndefined } from '../../../Utils/notUndefined';
import dayjs from '../../../Utils/dayjs';
import { Dayjs } from 'dayjs';

export type WeekDayHourGroup = {
  hour: string;
  date: Dayjs;
  entries: WeekDayEntry[];
};

export type WeekDay = {
  date: string;
  entries: WeekDayEntry[];
  readonly dayjs: dayjs.Dayjs;
  readonly entriesSortedByTime: WeekDayEntry[];
  readonly entriesGroupedByHour: WeekDayHourGroup[];
  readonly firstUnfinishedEntry: WeekDayEntry | undefined;
  readonly isToday: boolean;
};

export function createWeekDay(json?: Partial<WeekDay>): WeekDay {
  return makeAutoObservable({
    date: json?.date ?? '',
    entries: (json?.entries ?? []).map((e) => WeekDayEntryBuilder.build(e)).filter(notUndefined),

    get dayjs(): dayjs.Dayjs {
      return dayjs(this.date, 'YYYY-MM-DD');
    },

    get entriesSortedByTime(): WeekDayEntry[] {
      return this.entries.slice().sort((a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf());
    },

    get entriesGroupedByHour(): WeekDayHourGroup[] {
      return this.entriesSortedByTime.reduce((agg, entry) => {
        const hour = dayjs(entry.startTime).format('HH:mm');
        const existing = agg.find((a) => a.hour === hour);
        if (existing) {
          existing.entries.push(entry);
        } else {
          agg.push({ hour, date: dayjs(entry.startTime), entries: [entry] });
        }
        return agg;
      }, [] as WeekDayHourGroup[]);
    },

    get firstUnfinishedEntry(): WeekDayEntry | undefined {
      if (this.isToday) {
        return this.entriesSortedByTime.find((e) => !e.showAsDone && !e.disabled);
      }
      return undefined;
    },

    get isToday(): boolean {
      return dayjs().format('YYYYMMDD') === this.dayjs.format('YYYYMMDD');
    },
  });
}
